// Spot Buy Modal - For buying more of existing spot assets
import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { X, TrendingUp, Wallet, DollarSign, AlertCircle } from 'lucide-react';
import { tradingAPI, userAPI } from '../../services/api';
import { useTradingToast } from '../../hooks/useTradingToast';

const SpotBuyModal = ({ isOpen, onClose, holding, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const { toast } = useTradingToast();

  useEffect(() => {
    if (isOpen && holding) {
      setAmount('');
      loadData();
    }
  }, [isOpen, holding]);

  const loadData = async () => {
    if (!holding) return;
    try {
      // Load current price
      const symbol = `${holding.symbol}USDT`;
      const priceRes = await tradingAPI.getPrice(symbol);
      setCurrentPrice(priceRes.data.price);

      // Load user balance
      const balanceRes = await userAPI.getBalance();
      setBalance(balanceRes.data.exchange_balance || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
      setCurrentPrice(holding.avg_buy_price);
    }
  };

  if (!isOpen || !holding) return null;

  const buyAmount = parseFloat(amount) || 0;
  const estimatedQuantity = currentPrice > 0 ? buyAmount / currentPrice : 0;

  const handleQuickAmount = (percent) => {
    const amt = balance * percent / 100;
    setAmount(amt.toFixed(2));
  };

  const handleSubmit = async () => {
    if (!amount || buyAmount <= 0) {
      toast.error('Введите сумму');
      return;
    }

    if (buyAmount > balance) {
      toast.error(`Недостаточно средств. Баланс: $${balance.toFixed(2)}`);
      return;
    }

    if (buyAmount < 10) {
      toast.error('Минимальная сумма: $10');
      return;
    }

    setLoading(true);
    try {
      await tradingAPI.spotBuy({
        symbol: `${holding.symbol}USDT`,
        amount_usdt: buyAmount,
      });

      toast.success(
        'Покупка выполнена', 
        `Куплено ${estimatedQuantity.toFixed(6)} ${holding.symbol} за $${buyAmount.toFixed(2)}`
      );

      onClose();
      onSuccess?.();
    } catch (error) {
      toast.apiError(error, 'Ошибка при покупке');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    if (price >= 1) return `$${price.toFixed(2)}`;
    return `$${price.toPrecision(4)}`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e2329',
          borderRadius: '16px',
          width: '420px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5)',
          border: '1px solid #2b3139',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #2b3139',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(180deg, #1f2b1f 0%, #1e2329 100%)',
        }}>
          <div>
            <h2 style={{
              margin: 0,
              color: '#eaecef',
              fontSize: '18px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <TrendingUp size={20} color="#0ecb81" />
              Купить {holding.symbol}
            </h2>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '4px',
            }}>
              <span style={{ color: '#848e9c', fontSize: '14px' }}>Текущая цена:</span>
              <span style={{
                color: '#f0b90b',
                fontSize: '16px',
                fontWeight: '700',
                fontFamily: 'monospace',
              }}>
                {formatPrice(currentPrice)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#2b3139',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: '#848e9c',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {/* Current Holdings Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            padding: '14px 16px',
            backgroundColor: '#161a1e',
            borderRadius: '10px',
            marginBottom: '16px',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <Wallet size={14} color="#848e9c" />
                <span style={{ color: '#848e9c', fontSize: '12px' }}>Текущий баланс:</span>
              </div>
              <span style={{ color: '#0ecb81', fontSize: '15px', fontWeight: '700', fontFamily: 'monospace' }}>
                ${balance.toFixed(2)}
              </span>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <DollarSign size={14} color="#848e9c" />
                <span style={{ color: '#848e9c', fontSize: '12px' }}>Уже имеете:</span>
              </div>
              <span style={{ color: '#eaecef', fontSize: '15px', fontWeight: '700', fontFamily: 'monospace' }}>
                {holding.quantity?.toFixed(6)} {holding.symbol}
              </span>
            </div>
          </div>

          {/* Amount Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: '#848e9c', fontSize: '12px', marginBottom: '6px', display: 'block' }}>
              Сумма покупки (USDT)
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '14px 80px 14px 16px',
                  backgroundColor: '#161a1e',
                  border: '1px solid #2b3139',
                  borderRadius: '10px',
                  color: '#eaecef',
                  fontSize: '18px',
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  outline: 'none',
                }}
              />
              <span style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#f0b90b',
                fontSize: '14px',
                fontWeight: '600',
              }}>
                USDT
              </span>
            </div>

            {/* Quick Amount Buttons */}
            <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  onClick={() => handleQuickAmount(pct)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: '#161a1e',
                    border: '1px solid #2b3139',
                    borderRadius: '8px',
                    color: '#848e9c',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#252930';
                    e.currentTarget.style.color = '#0ecb81';
                    e.currentTarget.style.borderColor = '#0ecb81';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#161a1e';
                    e.currentTarget.style.color = '#848e9c';
                    e.currentTarget.style.borderColor = '#2b3139';
                  }}
                >
                  {pct === 100 ? 'MAX' : `${pct}%`}
                </button>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          {buyAmount > 0 && (
            <div style={{
              backgroundColor: '#161a1e',
              borderRadius: '10px',
              padding: '14px 16px',
              marginBottom: '16px',
            }}>
              <div style={{ fontSize: '12px', color: '#848e9c', marginBottom: '12px', fontWeight: '600' }}>
                Детали покупки
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ color: '#848e9c', fontSize: '13px' }}>Сумма</span>
                <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600' }}>
                  ${buyAmount.toFixed(2)} USDT
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ color: '#848e9c', fontSize: '13px' }}>Цена покупки</span>
                <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace' }}>
                  {formatPrice(currentPrice)}
                </span>
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                paddingTop: '10px',
                borderTop: '1px solid #2b3139',
              }}>
                <span style={{ color: '#848e9c', fontSize: '13px' }}>Получите</span>
                <span style={{ color: '#0ecb81', fontSize: '15px', fontFamily: 'monospace', fontWeight: '700' }}>
                  ~{estimatedQuantity.toFixed(6)} {holding.symbol}
                </span>
              </div>
            </div>
          )}

          {/* Info */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '12px',
            backgroundColor: '#0ecb8115',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid #0ecb8130',
          }}>
            <AlertCircle size={16} color="#0ecb81" style={{ flexShrink: 0, marginTop: '2px' }} />
            <span style={{ fontSize: '12px', color: '#848e9c', lineHeight: '1.5' }}>
              Покупка будет выполнена по рыночной цене. Средняя цена вашего холдинга будет пересчитана автоматически.
            </span>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading || !amount || buyAmount <= 0 || buyAmount > balance || buyAmount < 10}
            style={{
              width: '100%',
              padding: '16px',
              background: loading || !amount || buyAmount <= 0 || buyAmount > balance || buyAmount < 10
                ? '#2b3139'
                : 'linear-gradient(180deg, #0ecb81 0%, #0ba369 100%)',
              color: loading || !amount || buyAmount <= 0 || buyAmount > balance || buyAmount < 10
                ? '#5e6673'
                : '#fff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading || !amount || buyAmount <= 0 || buyAmount > balance || buyAmount < 10
                ? 'not-allowed'
                : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.2s',
              boxShadow: loading || !amount ? 'none' : '0 4px 20px rgba(14, 203, 129, 0.3)',
            }}
          >
            {loading ? (
              'Выполнение...'
            ) : (
              <>
                <TrendingUp size={20} />
                Купить {holding.symbol}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpotBuyModal;
